import { css, html, LitElement, type PropertyValueMap } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";

import { styles as spotlightStyles } from "../../utils/spotlight";

const ELEMENT_NAME = "bp-literary-design";

const USX_ELEMENTS = new Set([
  "book",
  "cell",
  "chapter",
  "char",
  "figure",
  "group",
  "note",
  "optbreak",
  "para",
  "ref",
  "row",
  "sidebar",
  "table",
  "usx",
  "verse",
]);

@customElement(ELEMENT_NAME)
export class LiteraryDesign extends LitElement {
  // private contentNode?: Node;

  private contentRef: Ref<HTMLDivElement> = createRef();

  @property()
  reference: string | undefined;

  @property({ attribute: "title" })
  designTitle?: string;

  @property({ attribute: "excluded-key-paths" })
  excludedKeyPaths: string = "[]";

  @property({ attribute: "usx" })
  usx?: string;

  parseScript(script: string) {
    const parser = new DOMParser();
    const xmlDocument = parser.parseFromString(script ?? "", "text/xml");
    const usx = xmlDocument.firstElementChild;
    if (!usx || usx.tagName !== "usx") {
      throw new Error(`${ELEMENT_NAME}: Missing required <usx> tag`);
    }
    const root = map(
      usx,
      (node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (USX_ELEMENTS.has(node.nodeName)) {
            return document.createElement(`${ELEMENT_NAME}-${node.nodeName}`);
          }
        }
        return node.cloneNode();
      },
      (_, attr) => {
        if (attr.name === "id") {
          return createAttribute("data-id", attr.value);
        }
        if (attr.name === "style") {
          return createAttribute(
            "data-style",
            attr.value
              .split(" ")
              // Styles are wrapped in _ to allow using attribute selectors to
              // accurately select single letter styles using the * selector.
              // For example, style="ord bk" would be incorrectly matched by [data-style*="k"]
              // if you were only trying to select styles with a single "k" such as style="ord k".
              .map((s: string) => `_${s}_`)
              .join(" "),
          );
        }
        return attr.cloneNode(true) as Attr;
      },
    );
    decorateElementKeyPath(root, `${ELEMENT_NAME}-group`.toUpperCase());
    this.contentRef?.value?.lastChild?.remove();
    this.contentRef?.value?.appendChild(root);
  }

  firstUpdated(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (changedProperties.has("usx") && this.usx) {
      this.parseScript(this.usx);
    }
  }

  willUpdate(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (changedProperties.has("usx")) {
      this.parseScript(this.usx ?? "");
    }
  }

  getElementById(id: string): HTMLElement | null {
    return this.shadowRoot?.querySelector(`[data-id="${id}"]`) ?? null;
  }

  getElementByKeyPath(keyPath: string): HTMLElement | null {
    return (
      this.shadowRoot?.querySelector(`[data-key-path="${keyPath}"]`) ?? null
    );
  }

  removeElementByKeyPath(keyPath: string): void {
    const element = this.shadowRoot?.querySelector(
      `[data-key-path="${keyPath}"]`,
    );
    if (!element) return;
    element.classList.add("hidden-group");
    if (
      element.nextElementSibling?.nodeName === "BP-LITERARY-DESIGN-OPTBREAK"
    ) {
      element.nextElementSibling.classList.add("hidden-group");
    }
    if (
      element.previousElementSibling?.nodeName === "BP-LITERARY-DESIGN-OPTBREAK"
    ) {
      element.previousElementSibling.classList.add("hidden-group");
    }
  }

  updated() {
    this.shadowRoot
      ?.querySelectorAll(".hidden-group")
      .forEach((query) => query.classList.remove("hidden-group"));
    console.log("Excluded paths", this.excludedKeyPaths);
    const keyPathsArray: Array<string> =
      this.excludedKeyPaths
        .match(/\[(?<stringArray>.*)\]/)
        ?.groups?.stringArray.split(",")
        .map((arrayValue) => arrayValue.trim()) ?? [];
    keyPathsArray.forEach((path) => this.removeElementByKeyPath(path));
  }

  render() {
    return html`
      <div>
        ${this.designTitle
          ? html`<div class="title">${this.designTitle}</div>`
          : html`<div class="design-start"></div>`}
        ${this.reference
          ? html`<div class="reference">${this.reference}</div>`
          : null}
        <div ${ref(this.contentRef as any)}></div>
        <slot name="caption"></slot>
      </div>
    `;
  }

  static styles = [
    spotlightStyles,
    css`
      :host {
        --title-font-size: 22px;
        --reference-font-size: 18px;
      }

      bp-literary-design-usx {
        --group-sidebar-color-offset: 6px;
        --group-sidebar-color-width: 4px;
        --group-sidebar-value-offset: 8px;
        --group-body-font-size: 16px;
        --group-title-font-size: 14px;
        --group-sidebar-font-size: 13px;
        --verse-font-size: 12px;
        line-height: var(--line-height-normal);
      }

      /* Used because otherwise pseudo-elements will not be included
        in height calculation when no title. */
      .design-start {
        height: 1px;
      }

      .title {
        font-size: var(--title-font-size);
        font-weight: var(--font-weight-normal);
        line-height: var(--line-height-snug);
      }

      .reference {
        font-size: var(--reference-font-size);
        font-weight: var(--font-weight-bold);
      }

      .hidden-group {
        display: none;
      }

      .hidden-optbreak {
        display: none;
      }

      bp-literary-design-para {
        display: block;
      }

      bp-literary-design-group {
        font-size: var(--group-body-font-size);
        border-radius: var(--radius-lg);
        display: block;
        padding: 0;
        position: relative;
      }

      bp-literary-design-group[title] {
        margin-top: var(--group-title-height);
      }

      bp-literary-design-group[type="primary"],
      bp-literary-design-group[type="primary-alt"],
      bp-literary-design-group[type="secondary"] {
        box-sizing: border-box;
        margin: var(--size-1-5) 0;
        padding: var(--size-1-5);
      }

      bp-literary-design-group[type="primary-alt"] {
        padding: var(--size-1-5) 0 0;
      }

      bp-literary-design-group
        > bp-literary-design-group:last-of-type[type="primary"],
      bp-literary-design-group
        > bp-literary-design-group:last-of-type[type="primary-alt"],
      bp-literary-design-group
        > bp-literary-design-group:last-of-type[type="secondary"] {
        margin-bottom: 0;
      }

      bp-literary-design-group[type="primary"][title],
      bp-literary-design-group[type="primary-alt"][title],
      bp-literary-design-group[type="secondary"][title] {
        border-radius: 0 var(--radius-lg) var(--radius-lg) var(--radius-lg);
        margin-top: calc(var(--group-title-height) + var(--size-1-5));
      }

      bp-literary-design-group
        > bp-literary-design-group:first-of-type[type="primary"][title],
      bp-literary-design-group
        > bp-literary-design-group:first-of-type[type="primary-alt"][title],
      bp-literary-design-group
        > bp-literary-design-group:first-of-type[type="secondary"][title] {
        margin-top: var(--group-title-height);
      }

      bp-literary-design-group[type="primary-alt"][title] {
        border-radius: 0;
      }

      bp-literary-design-group[type="primary"][title]::before,
      bp-literary-design-group[type="primary-alt"][title]::before,
      bp-literary-design-group[type="secondary"][title]::before,
      bp-literary-design-group[type="bare"][title]::before {
        border-radius: var(--radius-lg) var(--radius-lg) 0 0;
        bottom: 100%;
        box-sizing: border-box;
        content: attr(title);
        display: block;
        font-size: var(--group-title-font-size);
        font-weight: var(--font-weight-semibold);
        left: -2px;
        margin-right: var(--size-2);
        padding: var(--size-0-5) var(--size-1);
        position: absolute;
        z-index: 1;
      }

      bp-literary-design-group[type="bare"][title]::before {
        padding: var(--size-0-5) 0;
      }

      bp-literary-design-group::before {
        transition:
          color var(--spotlight-transition-duration),
          background-color var(--spotlight-transition-duration);
      }

      bp-literary-design-group[type="primary"][title]::before {
        color: white;
        background-color: var(--color-black);
      }

      bp-literary-design-group[type="primary-alt"][title]::before {
        color: var(--color-black);
        padding: var(--size-0-5) var(--size-0-5);
      }

      bp-literary-design-group[type="secondary"][title]::before {
        background-color: var(--color-neutral-10);
        color: var(--color-black);
      }

      bp-literary-design-group {
        color: var(--color-black);
        transition:
          color var(--spotlight-transition-duration),
          border var(--spotlight-transition-duration);
      }

      bp-literary-design-group[type="primary"] {
        border: 2px solid var(--color-black);
      }

      bp-literary-design-group[type="primary-alt"] {
        border-top: 2px solid var(--color-black);
        border-radius: 0px;
      }

      bp-literary-design-group[type="secondary"] {
        border: 2px solid var(--color-neutral-10);
      }

      bp-literary-design-group::after {
        transition: background var(--spotlight-transition-duration);
      }

      bp-literary-design-group[sidebar-color]::after {
        background: var(--color-black);
        border-radius: 100px;
        bottom: 0;
        content: "";
        display: block;
        inset-inline-start: 0;
        position: absolute;
        top: 0;
        transform: translateX(calc(-100% - var(--group-sidebar-color-offset)));
        width: var(--group-sidebar-color-width);
      }

      [dir="rtl"] bp-literary-design-group[sidebar-color]::after {
        transform: translateX(calc(100% + var(--group-sidebar-color-offset)));
      }

      bp-literary-design-group[sidebar-color="red"]::after {
        background: var(--color-red-50);
      }

      bp-literary-design-group[sidebar-color="orange"]::after {
        background: var(--color-orange-50);
      }

      bp-literary-design-group[sidebar-color="green"]::after {
        background: var(--color-green-50);
      }

      bp-literary-design-group[sidebar-color="turquoise"]::after {
        background: var(--color-turquoise-50);
      }

      bp-literary-design-group[sidebar-color="cyan"]::after {
        background: var(--color-cyan-50);
      }

      bp-literary-design-group[sidebar-color="blue"]::after {
        background: var(--color-blue-50);
      }

      bp-literary-design-group[sidebar-color="violet"]::after {
        background: var(--color-violet-50);
      }

      bp-literary-design-group[sidebar-color="purple"]::after {
        background: var(--color-purple-50);
      }

      bp-literary-design-group[sidebar-color="tan"]::after {
        background: var(--color-tan-50);
      }

      bp-literary-design-group[indent="1"] {
        margin-inline-start: 1.5rem;
      }

      bp-literary-design-group[indent="2"] {
        margin-inline-start: 3rem;
      }

      bp-literary-design-group[indent="3"] {
        margin-inline-start: 4.5rem;
      }

      bp-literary-design-group[indent="4"] {
        margin-inline-start: 6rem;
      }

      bp-literary-design-group[indent="5"] {
        margin-inline-start: 7.5rem;
      }

      bp-literary-design-group-sidebar-value {
        transition: color var(--spotlight-transition-duration);
        color: var(--color-gray);
        font-size: var(--group-sidebar-font-size);
        display: block;
        inset-inline-start: 0;
        padding-bottom: 2px;
        position: absolute;
        top: 50%;
        transform: translate(
          calc(-100% - var(--group-sidebar-value-offset)),
          -50%
        );
      }

      [dir="rtl"] bp-literary-design-group-sidebar-value {
        transform: translate(
          calc(100% + var(--group-sidebar-value-offset)),
          -50%
        );
      }

      bp-literary-design-group[sidebar-color]
        bp-literary-design-group-sidebar-value {
        transform: translate(
          calc(
            -100% - var(--group-sidebar-color-width) - var(
                --group-sidebar-color-offset
              ) - var(--group-sidebar-value-offset)
          ),
          -50%
        );
      }

      bp-literary-design-char {
        transition: color var(--spotlight-transition-duration);
        color: var(--color-black);
      }

      bp-literary-design-char[data-style="_em_"] {
        color: inherit;
        font-style: italic;
      }

      bp-literary-design-verse {
        font-size: var(--verse-font-size);
        vertical-align: 4px;
      }

      bp-literary-design-optbreak {
        transition: border var(--spotlight-transition-duration);
        border-bottom: 2px solid var(--color-neutral-10);
        display: block;
        margin: var(--size-1-5) 0;
      }

      bp-literary-design-note {
        display: none;
      }

      bp-literary-design-word-transliteration {
        font-style: italic;
      }

      bp-literary-design-word {
        white-space: nowrap;
      }
    `,
  ];
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-literary-design": LiteraryDesign;
  }
}

window.customElements.define(
  `${ELEMENT_NAME}-group`,
  class extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute("title")) {
        this.style.setProperty(
          "--group-title-height",
          window.getComputedStyle(this, ":before").height,
        );
      }
      if (this.hasAttribute("sidebar-value")) {
        const element = document.createElement(
          `${ELEMENT_NAME}-group-sidebar-value`,
        );
        (element as any).innerText = this.getAttribute("sidebar-value");
        this.insertBefore(element, this.firstChild);
      }
    }
  },
);

window.customElements.define(
  `${ELEMENT_NAME}-verse`,
  class extends HTMLElement {
    connectedCallback() {
      const verse = this.getAttribute("number") ?? "";
      this.innerText = verse;
    }
  },
);

window.customElements.define(
  `${ELEMENT_NAME}-char`,
  class extends HTMLElement {
    connectedCallback() {
      const wordLang = this.getAttribute("word-lang");
      const wordOriginal = this.getAttribute("word-original");
      const wordTransliteration = this.getAttribute("word-transliteration");
      if (wordOriginal || wordTransliteration) {
        const html: string[] = [];
        if (wordOriginal) {
          html.push(
            `<bp-text-${wordLang}>${wordOriginal}</bp-text-${wordLang}>`,
          );
        }
        if (wordTransliteration) {
          html.push(
            `<${ELEMENT_NAME}-word-transliteration>${wordTransliteration}</${ELEMENT_NAME}-word-transliteration>`,
          );
        }
        if (html.length) {
          const element = document.createElement(`${ELEMENT_NAME}-word`);
          element.innerHTML = `&nbsp;(${html.join(" • ")})`;
          this.appendChild(element);
        }
      }
      const highlightShown = this.getAttribute("highlight-show");
      const highlightColor = this.getAttribute("highlight-color");
      const highlightRevealTime = this.getAttribute("highlight-reveal-time");
      if (highlightShown || highlightRevealTime || highlightColor) {
        const element = document.createElement("bp-highlight");
        highlightRevealTime &&
          element.setAttribute("reveal-time", highlightRevealTime);
        highlightColor && element.setAttribute("color", highlightColor);
        element.setAttribute("can-spotlight", "true");
        element.setAttribute("show", highlightShown ?? "false");
        element.setAttribute("data-id", this.getAttribute("data-id") ?? "");
        this.setAttribute("data-id", "");
        for (const node of Array.from(this.childNodes)) {
          element.appendChild(node);
        }
        this.appendChild(element);
      }
      const markVariant = this.getAttribute("mark-variant");
      const markColor = this.getAttribute("mark-color");
      if (markVariant || markColor) {
        const element = document.createElement("bp-mark");
        element.setAttribute("color", markColor ?? "");
        element.setAttribute("variant", markVariant ?? "");
        for (const node of Array.from(this.childNodes)) {
          element.appendChild(node);
        }
        this.appendChild(element);
      }
    }
  },
);

function createAttribute(name: string, value: string) {
  const attr = document.createAttribute(name);
  attr.value = value;
  return attr;
}

function decorateElementKeyPath(rootNode: Node, elementName: string) {
  const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_ELEMENT);
  let currentParentNode: Node | null = null;
  let currentRootParentNode: Node | null = null;
  let keyPath: { value: number }[] = [];
  const keyPathIndexes = new Map<Node, number>();
  const keyPathString = () => keyPath.map((k) => k.value).join(".");
  const dataIdString = () => "group-" + keyPath.map((k) => k.value).join("-");
  while (walker.nextNode()) {
    if (walker.currentNode.nodeName !== elementName) continue;
    const parentNode = walker.currentNode.parentNode;
    if (!currentParentNode || parentNode !== currentParentNode) {
      if (parentNode && keyPathIndexes.has(parentNode)) {
        keyPath = keyPath.slice(0, (keyPathIndexes.get(parentNode) ?? 0) + 1);
        keyPath[keyPath.length - 1].value += 1;
      } else {
        if (
          !currentRootParentNode ||
          !currentRootParentNode.contains(walker.currentNode)
        ) {
          if (keyPath.length) {
            keyPath = keyPath.slice(0, 1);
            keyPath[keyPath.length - 1].value += 1;
          } else {
            keyPath.push({ value: 1 });
          }
          currentRootParentNode = parentNode;
        } else {
          keyPath.push({ value: 1 });
        }
        if (parentNode) {
          keyPathIndexes.set(parentNode, keyPath.length - 1);
        }
      }
      currentParentNode = parentNode;
    } else {
      keyPath[keyPath.length - 1].value += 1;
    }
    (walker.currentNode as HTMLElement).setAttribute(
      "data-key-path",
      keyPathString(),
    );
    (walker.currentNode as HTMLElement).setAttribute("data-id", dataIdString());
  }
}

function map(
  node: Node,
  mapNode: (node: Node) => Node,
  mapAttribute: (node: Node, attr: Attr) => Attr | null,
): Node {
  const newNode = mapNode(node);
  node.childNodes.forEach((childNode) => {
    newNode.appendChild(map(childNode, mapNode, mapAttribute));
  });
  if (
    node.nodeType === Node.ELEMENT_NODE &&
    newNode.nodeType === Node.ELEMENT_NODE
  ) {
    const nodeElement = node as Element;
    const newNodeElement = newNode as Element;
    for (let i = 0, l = nodeElement.attributes.length; i < l; i++) {
      const attr = nodeElement.attributes.item(i);
      if (!attr) continue;
      const newAttr = mapAttribute(node, attr);
      if (!newAttr) continue;
      newNodeElement.setAttributeNode(newAttr);
    }
  }
  return newNode;
}
